(() => {
  const STORAGE_KEY = 'dyeforWishlistV1';

  class DyeforWishlist {
    constructor() {
      this.items = this.read();
      this.lastFocused = null;
      this.syncQueued = false;

      document.addEventListener('click', this.onClick.bind(this));
      document.addEventListener('keydown', this.onKeydown.bind(this));
      document.addEventListener('shopify:section:load', () => this.refresh());
      window.addEventListener('storage', (event) => {
        if (event.key !== STORAGE_KEY) return;
        this.items = this.read();
        this.refresh();
      });

      this.observeNewProductCards();
      this.refresh();
    }

    read() {
      try {
        const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
        if (!Array.isArray(saved)) return [];

        return saved
          .filter((item) => item && item.id && item.title && item.url)
          .slice(0, 100)
          .map((item) => ({
            id: String(item.id),
            handle: String(item.handle || ''),
            title: String(item.title),
            url: String(item.url),
            image: String(item.image || ''),
            price: String(item.price || '')
          }));
      } catch (error) {
        return [];
      }
    }

    write() {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (error) {
        // The wishlist remains usable for the current page if storage is unavailable.
      }
    }

    productFromButton(button) {
      return {
        id: String(button.dataset.productId || ''),
        handle: String(button.dataset.productHandle || ''),
        title: String(button.dataset.productTitle || ''),
        url: String(button.dataset.productUrl || ''),
        image: String(button.dataset.productImage || ''),
        price: String(button.dataset.productPrice || '')
      };
    }

    toggle(button) {
      const product = this.productFromButton(button);
      if (!product.id || !product.title || !product.url) return;

      const existingIndex = this.items.findIndex((item) => item.id === product.id);
      const isAdding = existingIndex === -1;

      if (isAdding) {
        this.items.unshift(product);
      } else {
        this.items.splice(existingIndex, 1);
      }

      this.write();
      this.refresh();
      this.announce(isAdding ? `${product.title} added to your wishlist.` : `${product.title} removed from your wishlist.`);
    }

    remove(productId) {
      const product = this.items.find((item) => item.id === productId);
      this.items = this.items.filter((item) => item.id !== productId);
      this.write();
      this.refresh();

      if (product) this.announce(`${product.title} removed from your wishlist.`);
    }

    syncButtons() {
      const savedIds = new Set(this.items.map((item) => item.id));

      document.querySelectorAll('[data-dyefor-wishlist-toggle]').forEach((button) => {
        const isSaved = savedIds.has(String(button.dataset.productId || ''));
        const productTitle = button.dataset.productTitle || 'this product';
        button.classList.toggle('is-active', isSaved);
        button.setAttribute('aria-pressed', String(isSaved));
        button.setAttribute('aria-label', `${isSaved ? 'Remove' : 'Save'} ${productTitle} ${isSaved ? 'from' : 'to'} wishlist`);
        button.title = isSaved ? 'Remove from wishlist' : 'Save to wishlist';
      });
    }

    refresh() {
      this.syncButtons();

      document.querySelectorAll('[data-dyefor-wishlist-count]').forEach((count) => {
        count.hidden = this.items.length === 0;
        count.textContent = this.items.length > 99 ? '99+' : String(this.items.length);
      });

      document.querySelectorAll('[data-dyefor-wishlist-open]').forEach((button) => {
        const itemLabel = this.items.length === 1 ? 'item' : 'items';
        button.setAttribute('aria-label', `Open wishlist, ${this.items.length} saved ${itemLabel}`);
      });

      this.renderModal();
    }

    renderModal() {
      const modal = document.querySelector('[data-dyefor-wishlist-modal]');
      if (!modal) return;

      const empty = modal.querySelector('[data-dyefor-wishlist-empty]');
      const saved = modal.querySelector('[data-dyefor-wishlist-saved]');
      const count = modal.querySelector('[data-dyefor-wishlist-saved-count]');
      const grid = modal.querySelector('[data-dyefor-wishlist-grid]');

      if (!empty || !saved || !count || !grid) return;

      const hasItems = this.items.length > 0;
      empty.hidden = hasItems;
      saved.hidden = !hasItems;
      count.textContent = `${this.items.length} saved ${this.items.length === 1 ? 'gift' : 'gifts'}`;
      grid.replaceChildren(...this.items.map((item) => this.createCard(item)));
    }

    createCard(item) {
      const card = document.createElement('article');
      card.className = 'dyefor-wishlist-card';
      card.setAttribute('role', 'listitem');

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'dyefor-wishlist-card__remove';
      remove.dataset.dyeforWishlistRemove = item.id;
      remove.setAttribute('aria-label', `Remove ${item.title} from wishlist`);
      remove.title = 'Remove from wishlist';
      remove.innerHTML = '<svg class="icon icon-heart" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      const link = document.createElement('a');
      link.className = 'dyefor-wishlist-card__link';
      link.href = item.url;

      const media = document.createElement('div');
      media.className = 'dyefor-wishlist-card__media';

      if (item.image) {
        const image = document.createElement('img');
        image.className = 'dyefor-wishlist-card__image';
        image.src = item.image;
        image.alt = item.title;
        image.loading = 'lazy';
        image.width = 600;
        image.height = 600;
        media.append(image);
      }

      const content = document.createElement('div');
      content.className = 'dyefor-wishlist-card__content';

      const title = document.createElement('h3');
      title.className = 'dyefor-wishlist-card__title';
      title.textContent = item.title;
      content.append(title);

      if (item.price) {
        const price = document.createElement('span');
        price.className = 'dyefor-wishlist-card__price';
        price.textContent = item.price;
        content.append(price);
      }

      link.append(media, content);
      card.append(remove, link);
      return card;
    }

    open(opener) {
      const modal = document.querySelector('[data-dyefor-wishlist-modal]');
      if (!modal) return;

      document.querySelectorAll('details-modal.header__search').forEach((searchModal) => {
        if (searchModal.querySelector('details[open]')) searchModal.close(false);
      });

      this.lastFocused = opener || document.activeElement;
      this.renderModal();
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      modal.querySelector('button[data-dyefor-wishlist-close]')?.focus();
    }

    close() {
      const modal = document.querySelector('[data-dyefor-wishlist-modal]');
      if (!modal || modal.hidden) return;

      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');

      if (this.lastFocused && document.contains(this.lastFocused)) this.lastFocused.focus();
    }

    announce(message) {
      const status = document.querySelector('[data-dyefor-wishlist-status]');
      if (!status) return;
      status.textContent = '';
      window.setTimeout(() => {
        status.textContent = message;
      }, 20);
    }

    onClick(event) {
      const toggle = event.target.closest('[data-dyefor-wishlist-toggle]');
      if (toggle) {
        event.preventDefault();
        event.stopPropagation();
        this.toggle(toggle);
        return;
      }

      const opener = event.target.closest('[data-dyefor-wishlist-open]');
      if (opener) {
        event.preventDefault();
        this.open(opener);
        return;
      }

      const remove = event.target.closest('[data-dyefor-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        this.remove(String(remove.dataset.dyeforWishlistRemove));
        return;
      }

      if (event.target.closest('[data-dyefor-wishlist-close]')) {
        event.preventDefault();
        this.close();
      }
    }

    onKeydown(event) {
      const modal = document.querySelector('[data-dyefor-wishlist-modal]');
      if (!modal || modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(modal.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((element) => !element.hidden && element.getClientRects().length > 0);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    observeNewProductCards() {
      if (!document.body || !window.MutationObserver) return;

      const observer = new MutationObserver((mutations) => {
        if (!mutations.some((mutation) => mutation.addedNodes.length)) return;
        if (this.syncQueued) return;

        this.syncQueued = true;
        window.requestAnimationFrame(() => {
          this.syncQueued = false;
          this.syncButtons();
        });
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (window.dyeforWishlist) {
    window.dyeforWishlist.refresh();
  } else {
    window.dyeforWishlist = new DyeforWishlist();
  }
})();
